import React, { useState, useEffect } from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  PDFViewer,
  PDFDownloadLink,
  Font,
} from "@react-pdf/renderer";

import CudelLogo from "../../../../../assets/images/cudel_logo.png";
import AuthorizedSignature from "../../../../../assets/images/authorized_signature.png";

// ── Font Registration (NotoSans supports ₹ and all Indian currency glyphs) ──

Font.register({
  family: 'NotoSans',
  fonts: [
    {
      src: 'https://cdn.jsdelivr.net/npm/@fontsource/noto-sans/files/noto-sans-all-400-normal.woff',
      fontWeight: 'normal',
    },
    {
      src: 'https://cdn.jsdelivr.net/npm/@fontsource/noto-sans/files/noto-sans-all-700-normal.woff',
      fontWeight: 'bold',
    },
  ],
});

// ── helpers ──────────────────────────────────────────────────────────────────

const ImageAsBase64 = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error loading image:", error);
    return null;
  }
};

// ── styles ────────────────────────────────────────────────────────────────────

const colors = {
  primary: "#1a1a2e",
  accent: "#0f3460",
  lightGray: "#f5f5f5",
  borderGray: "#e0e0e0",
  textDark: "#222222",
  textMid: "#555555",
  white: "#ffffff",
};

const styles = StyleSheet.create({
  page: {
    fontFamily: "NotoSans",
    fontSize: 9,
    color: colors.textDark,
    backgroundColor: colors.white,
    paddingTop: 30,
    paddingBottom: 40,
    paddingHorizontal: 36,
  },

  // ── header ──
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 6,
  },
  logo: { width: 90, height: 36, objectFit: "contain" },
  receiptBadge: { alignItems: "flex-end" },
  receiptTitle: {
    fontSize: 22,
    fontFamily: "NotoSans",
    fontWeight: "bold",
    color: colors.primary,
    letterSpacing: 1,
  },
  receiptSubtitle: {
    fontSize: 8,
    color: colors.textMid,
    marginTop: 2,
    letterSpacing: 0.5,
  },

  // ── issuer block ──
  issuerBlock: { marginBottom: 10 },
  issuerText: { fontSize: 8, color: colors.textMid, lineHeight: 1.5 },
  gstinText: {
    fontSize: 8,
    fontFamily: "NotoSans",
    fontWeight: "bold",
    color: colors.textDark,
    marginTop: 3,
  },

  // ── divider ──
  divider: {
    borderBottomWidth: 1.5,
    borderBottomColor: colors.accent,
    marginBottom: 14,
  },
  thinDivider: {
    borderBottomWidth: 0.5,
    borderBottomColor: colors.borderGray,
    marginBottom: 10,
  },

  // ── two-column info section ──
  infoRow: { flexDirection: "row", gap: 16, marginBottom: 14 },
  infoBox: {
    flex: 1,
    backgroundColor: colors.lightGray,
    borderRadius: 4,
    padding: 10,
  },
  infoBoxTitle: {
    fontSize: 9,
    fontFamily: "NotoSans",
    fontWeight: "bold",
    color: colors.accent,
    borderBottomWidth: 0.5,
    borderBottomColor: colors.borderGray,
    paddingBottom: 4,
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  infoField: { marginBottom: 4 },
  infoLabel: {
    fontSize: 7.5,
    fontFamily: "NotoSans",
    fontWeight: "bold",
    color: colors.textMid,
    marginBottom: 1,
  },
  infoValue: { fontSize: 8.5, color: colors.textDark },

  // ── table ──
  tableContainer: { marginBottom: 16 },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: colors.accent,
    borderRadius: 3,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tableHeaderCell: {
    color: colors.white,
    fontSize: 8,
    fontFamily: "NotoSans",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: colors.borderGray,
    paddingVertical: 7,
    paddingHorizontal: 8,
    backgroundColor: colors.white,
  },
  tableRowAlt: { backgroundColor: "#fafafa" },
  tableCell: { fontSize: 8.5, color: colors.textDark },

  // column widths
  colService: { flex: 2 },
  colRate: { flex: 1.2, textAlign: "right" },
  colTaxable: { flex: 1.5, textAlign: "center" },
  colIGST: { flex: 1, textAlign: "right" },
  colCGST: { flex: 1, textAlign: "right" },
  colSGST: { flex: 1, textAlign: "right" },
  colSubtotal: { flex: 1.3, textAlign: "right" },

  // ── totals ──
  totalsContainer: {
    alignItems: "flex-end",
    marginBottom: 20,
  },
  totalsBox: {
    width: 220,
    borderWidth: 0.5,
    borderColor: colors.borderGray,
    borderRadius: 4,
    overflow: "hidden",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: colors.borderGray,
  },
  totalRowFinal: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: colors.accent,
  },
  totalLabel: { fontSize: 8, color: colors.textMid },
  totalValue: { fontSize: 8, color: colors.textDark },
  totalLabelFinal: {
    fontSize: 9,
    fontFamily: "NotoSans",
    fontWeight: "bold",
    color: colors.white,
  },
  totalValueFinal: {
    fontSize: 9,
    fontFamily: "NotoSans",
    fontWeight: "bold",
    color: colors.white,
  },

  // ── footer / signature ──
  footerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 10,
  },
  remarksBox: { flex: 1, marginRight: 20 },
  remarksLabel: {
    fontSize: 8,
    fontFamily: "NotoSans",
    fontWeight: "bold",
    color: colors.textMid,
    marginBottom: 3,
  },
  remarksText: { fontSize: 8, color: colors.textDark },
  signatureBlock: { alignItems: "center", width: 130 },
  signatureImage: { width: 80, height: 36, objectFit: "contain", marginBottom: 4 },
  signatureLine: {
    borderTopWidth: 0.75,
    borderTopColor: colors.textDark,
    width: 120,
    marginBottom: 4,
  },
  signatureLabel: {
    fontSize: 8,
    fontFamily: "NotoSans",
    fontWeight: "bold",
    color: colors.textDark,
    textAlign: "center",
  },

  // ── payment mode pill ──
  paymentPill: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
  },
  paymentLabel: { fontSize: 7.5, fontFamily: "NotoSans", fontWeight: "bold", color: colors.textMid },
  paymentValue: {
    fontSize: 7.5,
    color: colors.white,
    backgroundColor: colors.accent,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    marginLeft: 4,
  },
});

// ── PDF Document ──────────────────────────────────────────────────────────────

const CuDelReceipt = ({ data, logoBase64, signatureBase64 }) => {
  const fmt = (v) => `\u20B9${parseFloat(v).toFixed(2)}`;

  return (
    <Document title={`Receipt-${data.invoiceNo}`}>
      <Page size="A4" style={styles.page}>

        {/* ── Header ── */}
        <View style={styles.headerRow}>
          <View>
            {logoBase64 ? (
              <Image style={styles.logo} src={logoBase64} />
            ) : (
              <Text style={{ fontSize: 18, fontFamily: "NotoSans", fontWeight: "bold", color: colors.accent }}>
                CuDel
              </Text>
            )}
            <View style={styles.issuerBlock}>
              <Text style={styles.issuerText}>B-154, B Block, Sector 63, Noida,</Text>
              <Text style={styles.issuerText}>Uttar Pradesh(09) - 201301</Text>
              <Text style={styles.gstinText}>GSTIN: 09AAHCC6070E1ZP</Text>
            </View>
          </View>
          <View style={styles.receiptBadge}>
            <Text style={styles.receiptTitle}>RECEIPT</Text>
            <Text style={styles.receiptSubtitle}>(CUDEL EXPERT RECEIPT)</Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* ── Invoice + Provider Info ── */}
        <View style={styles.infoRow}>
          {/* Invoice Details */}
          <View style={styles.infoBox}>
            <Text style={styles.infoBoxTitle}>Invoice Details</Text>

            <View style={styles.infoField}>
              <Text style={styles.infoLabel}>Invoice Number</Text>
              <Text style={styles.infoValue}>{data.invoiceNo}</Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoLabel}>Invoice Type</Text>
              <Text style={styles.infoValue}>{data.invoiceType}</Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoLabel}>Invoice Issue Date</Text>
              <Text style={styles.infoValue}>{data.payoutDate}</Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoLabel}>Place of Supply</Text>
              <Text style={styles.infoValue}>
                {data.placeOfSupply} ({data.stateCode})
              </Text>
            </View>
            <View style={styles.paymentPill}>
              <Text style={styles.paymentLabel}>Payment Mode: </Text>
              <Text style={styles.paymentValue}>{data.paymentMode}</Text>
            </View>
          </View>

          {/* Service Provider Details */}
          <View style={styles.infoBox}>
            <Text style={styles.infoBoxTitle}>Service Provider Details</Text>

            <View style={styles.infoField}>
              <Text style={styles.infoLabel}>Business GSTIN</Text>
              <Text style={styles.infoValue}>{data.gstin}</Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoLabel}>Expert Name</Text>
              <Text style={styles.infoValue}>{data.expertName}</Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoLabel}>Address</Text>
              <Text style={styles.infoValue}>{data.expertAddress}</Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoLabel}>City / Pincode</Text>
              <Text style={styles.infoValue}>
                {data.expertCity} - {data.expertPincode}
              </Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoLabel}>State Name &amp; Code</Text>
              <Text style={styles.infoValue}>
                {data.placeOfSupply} ({data.stateCode})
              </Text>
            </View>
          </View>
        </View>

        {/* ── Table ── */}
        <View style={styles.tableContainer}>
          {/* Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.colService]}>Service</Text>
            <Text style={[styles.tableHeaderCell, styles.colRate]}>Rate (₹)</Text>
            <Text style={[styles.tableHeaderCell, styles.colTaxable]}>Taxable Amt (₹)</Text>
            <Text style={[styles.tableHeaderCell, styles.colIGST]}>IGST (₹)</Text>
            <Text style={[styles.tableHeaderCell, styles.colCGST]}>CGST (₹)</Text>
            <Text style={[styles.tableHeaderCell, styles.colSGST]}>SGST (₹)</Text>
            <Text style={[styles.tableHeaderCell, styles.colSubtotal]}>Subtotal (₹)</Text>
          </View>

          {/* Row */}
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, styles.colService]}>{data.serviceType}</Text>
            <Text style={[styles.tableCell, styles.colRate]}>
              {parseFloat(data.taxableValue).toFixed(2)}
            </Text>
            <Text style={[styles.tableCell, styles.colTaxable]}>
              {parseFloat(data.taxableValue).toFixed(2)}
            </Text>
            <Text style={[styles.tableCell, styles.colIGST]}>
              {parseFloat(data.igst).toFixed(2)}
            </Text>
            <Text style={[styles.tableCell, styles.colCGST]}>
              {parseFloat(data.cgst).toFixed(2)}
            </Text>
            <Text style={[styles.tableCell, styles.colSGST]}>
              {parseFloat(data.sgst).toFixed(2)}
            </Text>
            <Text style={[styles.tableCell, styles.colSubtotal]}>
              {parseFloat(data.totalInvoiceValue).toFixed(2)}
            </Text>
          </View>
        </View>

        {/* ── Totals ── */}
        <View style={styles.totalsContainer}>
          <View style={styles.totalsBox}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Taxable Amount</Text>
              <Text style={styles.totalValue}>{fmt(data.taxableValue)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>GST Rate</Text>
              <Text style={styles.totalValue}>{parseFloat(data.gstRate).toFixed(0)}%</Text>
            </View>
            {parseFloat(data.igst) > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>IGST ({parseFloat(data.gstRate).toFixed(0)}%)</Text>
                <Text style={styles.totalValue}>{fmt(data.igst)}</Text>
              </View>
            )}
            {parseFloat(data.cgst) > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>
                  CGST ({(parseFloat(data.gstRate) / 2).toFixed(0)}%)
                </Text>
                <Text style={styles.totalValue}>{fmt(data.cgst)}</Text>
              </View>
            )}
            {parseFloat(data.sgst) > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>
                  SGST ({(parseFloat(data.gstRate) / 2).toFixed(0)}%)
                </Text>
                <Text style={styles.totalValue}>{fmt(data.sgst)}</Text>
              </View>
            )}
            <View style={styles.totalRowFinal}>
              <Text style={styles.totalLabelFinal}>Total Invoice Value</Text>
              <Text style={styles.totalValueFinal}>{fmt(data.totalInvoiceValue)}</Text>
            </View>
          </View>
        </View>

        <View style={styles.thinDivider} />

        {/* ── Footer / Signature ── */}
        <View style={styles.footerRow}>
          <View style={styles.remarksBox}>
            {data.remarks ? (
              <>
                <Text style={styles.remarksLabel}>Remarks</Text>
                <Text style={styles.remarksText}>{data.remarks}</Text>
              </>
            ) : null}
          </View>

          <View style={styles.signatureBlock}>
            {signatureBase64 && (
              <Image style={styles.signatureImage} src={signatureBase64} />
            )}
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Authorized Signatory</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export { CuDelReceipt };

// ── App wrapper ───────────────────────────────────────────────────────────────
// Import assets as modules — fetch() CANNOT resolve raw relative path strings.

const invoiceData = {
  payoutDate: "2026-04-01",
  invoiceNo: "230326290326EPI0312",
  invoiceType: "B2B",
  expertName: "Asha Verma",
  gstin: "N/A",
  expertAddress: "H99M+79Q, Sector 76, Noida, Uttar Pradesh, India",
  expertCity: "Noida",
  placeOfSupply: "Uttar Pradesh",
  stateCode: "09",
  expertPincode: "201009",
  taxableValue: "100.00",
  gstRate: "18.00",
  cgst: "9.00",
  sgst: "9.00",
  igst: "0.00",
  totalInvoiceValue: "118.00",
  serviceType: "Expert Platform Fee",
  paymentMode: "Bank Transfer",
  remarks: "",
};

export default function ExpertPlatformPdf({ data = invoiceData }) {
  const [logoBase64, setLogoBase64] = useState(null);
  const [signatureBase64, setSignatureBase64] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const loadImages = async () => {
      try {
        // CudelLogo & AuthorizedSignature are bundler-resolved URLs (not raw paths)
        const [logo, sig] = await Promise.all([
          ImageAsBase64(CudelLogo),
          ImageAsBase64(AuthorizedSignature),
        ]);
        setLogoBase64(logo);
        setSignatureBase64(sig);
      } catch (error) {
        console.error("Error loading images:", error);
      } finally {
        setReady(true);
      }
    };
    loadImages();
  }, []);

  if (!ready) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "sans-serif" }}>
        Loading receipt…
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", fontFamily: "sans-serif" }}>
      {/* toolbar */}
      <div style={{ padding: "10px 20px", background: "#0f3460", display: "flex", alignItems: "center", gap: 16 }}>
        <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>CuDel Receipt Preview</span>
        <PDFDownloadLink
          document={
            <CuDelReceipt
              data={data}
              logoBase64={logoBase64}
              signatureBase64={signatureBase64}
            />
          }
          fileName={`Expert-Platform-Tax-Receipt-${data.invoiceNo}.pdf`}
          style={{
            marginLeft: "auto",
            background: "#fff",
            color: "#0f3460",
            border: "none",
            borderRadius: 6,
            padding: "7px 18px",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            textDecoration: "none",
          }}
        >
          {({ loading }) => (loading ? "Generating…" : "⬇ Download PDF")}
        </PDFDownloadLink>
      </div>

      {/* viewer */}
      <PDFViewer style={{ flex: 1, border: "none" }}>
        <CuDelReceipt
          data={data}
          logoBase64={logoBase64}
          signatureBase64={signatureBase64}
        />
      </PDFViewer>
    </div>
  );
}